import { Injectable, signal, inject, isDevMode } from '@angular/core';
import Clarity from '@microsoft/clarity';
import { StorageService } from './storage.service';

const STORAGE_KEY = 'nw-hub-cookie-consent';
const CLARITY_PROJECT_ID = 'w8hw6q4lrb';

export type ConsentStatus = 'granted' | 'denied' | 'pending';

@Injectable({ providedIn: 'root' })
export class ConsentService {
  private storage = inject(StorageService);

  /** Current consent status — 'pending' means user hasn't chosen yet. */
  readonly status = signal<ConsentStatus>(
    (this.storage.getString(STORAGE_KEY) as ConsentStatus) ?? 'pending'
  );

  /** Whether the consent banner should be visible. */
  readonly showBanner = signal(this.status() === 'pending');

  /** Initialize Clarity in cookieless mode; grant cookies only if already consented. */
  initAnalytics(): void {
    if (isDevMode()) return;
    Clarity.init(CLARITY_PROJECT_ID);
    if (this.status() === 'granted') {
      Clarity.consent(true);
    }
  }

  /** User accepts analytics cookies. */
  accept(): void {
    this.update('granted');
    if (!isDevMode()) Clarity.consent(true);
  }

  /** User declines analytics cookies. */
  decline(): void {
    this.update('denied');
  }

  /** Reset preference and re-show the banner. */
  reset(): void {
    this.storage.remove(STORAGE_KEY);
    this.status.set('pending');
    this.showBanner.set(true);
  }

  private update(value: ConsentStatus): void {
    this.storage.setString(STORAGE_KEY, value);
    this.status.set(value);
    this.showBanner.set(false);
  }
}
